'use client';

import { type FC, useCallback, useState, useRef } from 'react';
import styles from './Combobox.module.css';
import {
  Flex,
  Pill,
  Button,
  ButtonGroup,
  Icon,
  Input,
  type ResultListItem,
} from '@/_components';
import type { ComboboxProps } from './Combobox.types';
import { getClasses } from '@/_utils/classnames';

export const Combobox: FC<ComboboxProps> = ({
  className,
  label,
  isLabelHidden,
  onClear,
  isDisabled,
  items = [],
  isLoading,
  selectedValue,
  iconLeft,
  onSelect,
  placeholder,
  value,
  setValue,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredItems: Array<ResultListItem> = items.filter((item) => {
    if (!value) return true;

    return item.name?.toLowerCase().includes(value.toLowerCase());
  });

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;

    setValue(newValue);
    setIsOpen(true);
  };

  const handleSelectItem = useCallback(
    (item: ResultListItem) => {
      setValue(item.name || '');
      setIsOpen(false);
      onSelect?.(item);
    },
    [onSelect, setValue],
  );

  const onBlur = () => setTimeout(() => setIsOpen(false), 240);

  const comboboxSelectedClasses: string = getClasses(styles.comboboxSelected, {
    [styles.comboboxSelectedLoading]: isLoading,
  });

  return (
    <Flex isYCentered={true} gap={2} className={className}>
      {selectedValue ? (
        <Flex
          isYCentered={true}
          gap={4}
          isBetween={true}
          className={comboboxSelectedClasses}
        >
          <Flex
            isYCentered={true}
            gap={4}
            className={styles.comboboxSelected__pill}
          >
            {iconLeft && <Icon size={20} name={iconLeft} />}
            <Pill
              size="large"
              isRound={true}
              isEllipsis={true}
              intent="secondary"
              label={selectedValue}
            />
          </Flex>
          {onClear && (
            <ButtonGroup>
              <Button
                value="Clear"
                iconLeft="trash"
                isIconOnly={true}
                isDisabled={isDisabled}
                onClick={onClear}
                intent="danger-secondary"
                size="tiny"
              />
            </ButtonGroup>
          )}
        </Flex>
      ) : (
        <Input
          label={label}
          isLabelHidden={isLabelHidden}
          ref={inputRef}
          autoComplete="off"
          value={value}
          isLoading={isLoading}
          onChange={handleInputChange}
          iconLeft={iconLeft}
          placeholder={placeholder}
          isDisabled={isDisabled}
          onFocus={() => setIsOpen(true)}
          onBlur={onBlur}
          results={isOpen ? filteredItems : []}
          role="combobox"
          onResultSelect={handleSelectItem}
        />
      )}
    </Flex>
  );
};
